import React from 'react';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom'; //useHistory
import { useTranslation } from 'react-i18next';

import { Button, Paper } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import InsertInvitationIcon from '@material-ui/icons/InsertInvitation';
import EmojiEventsIcon from '@material-ui/icons/EmojiEventsOutlined';
import PersonIcon from '@material-ui/icons/Person';

import Tooltip from 'components/Tooltip/Tooltip';
import { sqlDateToLabel } from 'helpers/helpers';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import { Permission, ArrayCheck } from 'helpers/permissions';
import UserMenu, { MenuAction } from 'components/UserMenu';
import Notification from 'components/Layout/Notification';
import Services from './Services';

import * as routes from 'routes';
import { UIStore } from 'stores/uiStore';
import { UserRoles, EdwardColors } from 'enums';
import { UserStoreKC } from 'stores/userStoreKC';

interface HeaderProps {
  userMode?: boolean;
  detail?: boolean;
  admin?: boolean;
  logo?: boolean;
  signatureOnly?: boolean;
}

const AppLogo = require('assets/edward-logo.png');
const AppLogoClosed = require('assets/edward-logo-closed.png');
const AppLogoSmall = require('assets/edward-logo-small.png');

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: (props: HeaderProps) => ({
      display: 'flex',
      flexDirection: 'column',
      zIndex: 11,
      background: props.detail && !props.userMode ? 'rgb(252, 252, 252)' : 'rgb(245, 245, 245)',
      boxShadow: '0 0 10px 1px rgba(0,0,0,0.13)',
      position: 'relative',
      [theme.breakpoints.down('sm')]: {
        position: 'sticky',
        top: 0,
      },
      '& + .main-scrollable-container': {
        [theme.breakpoints.down('xs')]: {
          paddingTop: props.userMode ? '16px' : undefined,
        },
      },
    }),
    headerWrapper: (props: HeaderProps) => ({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      paddingX: theme.spacing(2),
      backgroundColor: props.admin ? theme.palette.common.black : theme.palette.common.white,
      color: props.admin ? theme.palette.common.white : theme.palette.common.black,
      zIndex: 11,
      [theme.breakpoints.down('xs')]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
      },
    }),
    flexWrapper: {
      flex: 1,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      [theme.breakpoints.down('xs')]: {
        height: '40px',
      },
    },
    spacer: (props: HeaderProps) => ({
      maxWidth: 0,
      width: 0,
      [theme.breakpoints.down('sm')]: {
        minWidth: props.signatureOnly ? 0 : '56px',
        width: props.signatureOnly ? 0 : '56px',
      },
      [theme.breakpoints.down('xs')]: {
        minWidth: 0,
        width: 0,
      },
    }),
    logoWrapper: (props: HeaderProps) => ({
      backgroundColor: theme.palette.common.black,
      height: '74px',
      transition: props.detail ? '0.5s all' : 'none',
      '& a': {
        position: 'relative',
        backgroundPosition: '8px 0',
        background: `url(${AppLogoClosed.default}) no-repeat 0 0`,
        backgroundSize: '40px 40px',
        width: '171px',
      },
      '& a:after': {
        transition: props.detail ? '0.5s opacity' : 'none',
        display: 'inline-block',
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        backgroundPosition: '8px 0',
        background: `url(${AppLogo.default}) no-repeat 0 0`,
        backgroundSize: '155px 40px',
        width: '171px',
        height: '40px',
      },

      '&.open': {
        minWidth: '250px',
        maxWidth: '250px',
        '& a': {
          width: '171px',
          [theme.breakpoints.down('xs')]: {
            background: props.userMode ? `url(${AppLogoSmall.default}) no-repeat 0 0` : undefined,
            backgroundSize: props.userMode ? 'contain' : undefined,
            width: props.userMode ? '31px' : undefined,
            height: props.userMode ? '32px' : undefined,
          },
        },
        '& a:after': { opacity: 1 },
        [theme.breakpoints.down('xs')]: {
          minWidth: props.userMode ? '50px' : '171px',
          maxWidth: props.userMode ? '50px' : '171px',
        },
      },
      '&.closed': {
        minWidth: '56px',
        maxWidth: '56px',
        '& a': {
          [theme.breakpoints.down('xs')]: {
            background: props.userMode ? `url(${AppLogoSmall.default}) no-repeat 0 0` : undefined,
            backgroundSize: props.userMode ? 'contain' : undefined,
            width: props.userMode ? '31px' : undefined,
            height: props.userMode ? '32px' : '90%',
          },
        },
        '& a:after': {
          opacity: 0,
        },
        [theme.breakpoints.down('xs')]: {
          minWidth: props.userMode ? '50px' : '171px',
          maxWidth: props.userMode ? '50px' : '171px',
          '& a:after': {
            opacity: props.userMode ? 0 : 1,
          },
        },
      },
      [theme.breakpoints.down('sm')]: {
        position: props.signatureOnly ? 'relative' : 'absolute',
      },
      [theme.breakpoints.down('xs')]: {
        flex: 0,
        height: props.userMode ? '32px' : '50px',
        position: 'relative',
        '& a:after': {
          display: props.userMode ? 'none' : undefined,
        },
      },
    }),
    appLogo: (props: HeaderProps) => ({
      height: '40px',
      margin: '0 auto',

      [theme.breakpoints.down('xs')]: {
        background: props.userMode ? `url(${AppLogoSmall.default}) no-repeat 0 0` : undefined,
        backgroundSize: props.userMode ? 'contain' : undefined,
        width: props.userMode ? '31px' : undefined,
        height: props.userMode ? '32px' : '90%',
      },
    }),

    advisorData: (props: HeaderProps) => ({
      '&.userMode': {
        cursor: 'pointer',
        '&:hover': {
          color: EdwardColors.SECONDARY,
        },
      },
      flex: 0,
      padding: theme.spacing(0, 1, 0, 6),
      fontSize: '1.6rem',

      fontFamily: "'playfair_display_web', sans-serif",
      fontWeight: 'bold',

      '&:not(.advisorBar)': { [theme.breakpoints.down('xs')]: { display: 'none' } },

      lineHeight: '1.2',
      '& > div > em': {
        fontSize: '0.9rem',
        fontWeight: 'normal',
        lineHeight: '1rem',
        marginLeft: theme.spacing(1),
        fontStyle: 'normal',
      },
      '& img': {
        height: '60px',
        marginRight: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
          display: 'none',
        },
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.5rem',
        '& > div > em': {
          display: 'none',
        },
        '& span': {
          display: 'none',
        },
        '& > div': {
          lineHeight: '37px',
          textAlign: 'center',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          border: 'solid #fff 1px',
          margin: theme.spacing(0, 1),
          fontWeight: 'normal',
          [theme.breakpoints.down('xs')]: {
            width: '30px',
            height: '30px',
            lineHeight: '27px',
          },
        },
      },
      [theme.breakpoints.down('xs')]: {
        fontSize: '1.2rem',
      },
      '&.advisorBar': {
        flex: 1,
        whiteSpace: 'normal',
        alignSelf: 'center',
        fontSize: '20px',
        padding: theme.spacing(0, 1),
        '& em': {
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
        },
        [theme.breakpoints.down('xs')]: {
          lineHeight: '1.2rem',
        },
      },
      '@media (max-width: 400px)': {
        '&:not(.advisorBar)': { display: 'none' },
      },
    }),
    button: {
      margin: theme.spacing(0, 2),
    },
    helpButton: {
      marginRight: theme.spacing(1),
      borderColor: theme.palette.common.black,
      height: theme.spacing(4),
      '& svg': { color: theme.palette.common.black },
      [theme.breakpoints.down('xs')]: {
        '& svg': { color: EdwardColors.DARK },
        padding: theme.spacing('13px', 1),
        marginRight: 0,
        minWidth: '40px',
        '& .MuiButton-startIcon': {
          margin: 0,
        },
      },
      '@media (max-width: 400px)': {
        minWidth: '0',
        padding: theme.spacing(2, 0),
      },
    },
    iconLink: {
      color: EdwardColors.DARK,
      display: 'flex',
      '& a': {
        padding: theme.spacing(2, 2),
        display: 'inline-flex',
        color: EdwardColors.DARK,
        [theme.breakpoints.down('xs')]: {
          padding: theme.spacing('13px', 1),
        },
        '@media (max-width: 400px)': {
          padding: theme.spacing(2, 1),
        },
      },
    },
    tradingDay: (props: HeaderProps) => ({
      '& a': {
        color: props.admin ? EdwardColors.DARK : theme.palette.common.black,
        [theme.breakpoints.down('xs')]: {
          color: EdwardColors.DARK,
        },
      },
    }),

    advisorBar: {
      display: 'none',
      alignSelf: 'flex-start',
      background: '#fff',
      padding: theme.spacing(0.5, 2),
      boxShadow: '0 0 10px 1px rgba(0,0,0,0.13)',
      borderBottomLeftRadius: '16px',
      borderBottomRightRadius: '16px',
      minWidth: '640px',
      flexWrap: 'wrap',
      '& img': {
        height: '40px',
      },
      '& svg': {
        color: EdwardColors.GRAY_MEDIUM,
        cursor: 'pointer',
        alignSelf: 'center',
        marginLeft: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
          position: 'absolute',
          right: '18px',
        },
      },
      [theme.breakpoints.down('xs')]: {
        alignSelf: 'stretch',
        minWidth: '0',
        marginBottom: 0,

        display: 'inline-flex',
        paddingRight: theme.spacing(6),
      },
    },
  }),
);

interface Props {
  userStore?: UserStoreKC;
  uiStore?: UIStore;
  showLogoutDialog: () => void;
  admin?: boolean;
  signatureOnly?: boolean;
}

const Header: React.FC<Props> = ({ userStore, uiStore, showLogoutDialog, admin, signatureOnly }) => {
  const { t } = useTranslation();
  const { setHelpDialogShown } = uiStore!;
  const userMode = userStore!.checkPermissions(Permission.APP_ENDUSER);
  const { openInfoModal } = uiStore!.userMenuData;
  const { metadata } = userStore!;
  const { detail } = uiStore!;

  const handleUserMenuAction = (menuAction: MenuAction) => {
    if (menuAction === MenuAction.LOGOUT) showLogoutDialog();
  };

  let topRole = UserRoles.ADVISOR;

  if (userStore!.hasRoles(UserRoles.EDW_BACKOFFICE)) {
    topRole = UserRoles.EDW_BACKOFFICE;
  }
  if (userStore!.hasRoles(UserRoles.EDW_ADMIN)) {
    topRole = UserRoles.EDW_ADMIN;
  }
  const roleLabel = t(`user_role_${topRole.toLowerCase().replace(/:/g, '_')}`);

  const advisorData = userMode ? userStore?.advisorData() : userStore!.loggedUser;
  const advisorName = advisorData ? (
    <>
      {advisorData.firstname[0]}
      <span>{advisorData.firstname.slice(1)}</span>
      <span>&nbsp;</span>
      {advisorData.lastname[0]}
      <span>{advisorData.lastname.slice(1)}</span>
      <em>{roleLabel}</em>
    </>
  ) : (
    ''
  );

  const classes = useStyles({ userMode, admin, detail, logo: !!advisorData?.logo, signatureOnly });

  const showHelpDialog = () => {
    setHelpDialogShown(true);
  };

  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'), { noSsr: true });

  return (
    <Paper elevation={1} className={classes.root} square>
      {!!userStore?.loggedUser && <Notification userStore={userStore} />}
      <div className={classes.headerWrapper}>
        <div
          className={`${classes.flexWrapper} ${classes.logoWrapper} ${
            uiStore!.detailMenuOpen || !uiStore!.detail || signatureOnly ? 'open' : 'closed'
          }`}
        >
          <Link className={`${classes.appLogo}`} to={routes.HOME_PAGE} />
        </div>

        <div className={`${classes.flexWrapper} ${classes.spacer}`} />

        {advisorData && (
          <div className={`${classes.flexWrapper} ${classes.advisorData} ${userMode ? 'userMode' : ''}`}>
            {advisorData.logo && <img alt="" src={advisorData.logo} />}
            <div onClick={openInfoModal && userMode ? () => openInfoModal(true) : undefined}>{advisorName}</div>
          </div>
        )}

        <div className={`${classes.flexWrapper}`} />

        {userStore?.hasRoles(UserRoles.EDW_ADMIN) && (
          <Button color="primary" variant="outlined" component={Link} className={classes.button} to={routes.ADMIN}>
            {t('pageTitle_admin')}
          </Button>
        )}
        {userStore?.hasRoles(UserRoles.EDW_BACKOFFICE) && (
          <Button
            color="primary"
            variant="outlined"
            component={Link}
            className={classes.button}
            to={routes.BACK_OFFICE}
          >
            {t('pageTitle_backoffice')}
          </Button>
        )}
        {userStore?.hasRoles([UserRoles.EDW_BACKOFFICE, UserRoles.EDW_ADMIN], ArrayCheck.ANY) && (
          <Services />
        )}
        {!!userStore?.loggedUser && !userMode && (
          <>

            <div className={`${classes.iconLink}`}>
              <Link to={routes.ADVISOR_RANKINGS}>
                <EmojiEventsIcon />
              </Link>
            </div>
            <div className={`${classes.iconLink} ${classes.tradingDay}`}>
              {metadata && metadata.tradingDay ? (
                <Tooltip
                  title={`${t('backOffice_tradingDay')}: ${sqlDateToLabel(metadata.tradingDay, 'dd. MM. yyyy')}`}
                  placement="top"
                >
                  <a href="https://www.edwardinvest.cz/kalendar" target="_blank" rel="noopener noreferrer">
                    <InsertInvitationIcon />
                  </a>
                </Tooltip>
              ) : (
                <a href="https://www.edwardinvest.cz/kalendar" target="_blank" rel="noopener noreferrer">
                  <LoadingIndicator />
                </a>
              )}
            </div>
          </>
        )}

        {userStore?.hasRoles(UserRoles.ADVISOR) && (
          <Button
            startIcon={<HelpOutlineIcon />}
            className={classes.helpButton}
            onClick={() => showHelpDialog()}
            color="default"
            variant={mobile ? 'text' : 'outlined'}
          >
            {mobile ? '' : t('general_help')}
          </Button>
        )}
        <UserMenu onMenuAction={handleUserMenuAction} admin={admin} />
      </div>
      {advisorData?.logo && (
        <span className={classes.advisorBar}>
          <img alt="" src={advisorData.logo} />
          {advisorData && (
            <span className={`${classes.advisorData} advisorBar`}>
              {advisorData.firstname} {advisorData.lastname} <em>{roleLabel}</em>
            </span>
          )}
          {userMode && <PersonIcon onClick={() => openInfoModal && openInfoModal(true)} />}
        </span>
      )}
    </Paper>
  );
};

export default inject('userStore', 'uiStore')(observer(Header));
